import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import { tokens } from '../../components/forms/tokens';
import { getApiErrorMessage } from '../../lib/apiError';
import { readerLocale } from '../../i18n/language';
import productAccessService, { type ProductAccess } from '../../services/productAccess.service';

const REASON_MIN = 5;

/**
 * Super admin: close a product to new clients (0072). A closed product takes no
 * new contracts and no proposals to clients who do not hold it; clients who
 * already hold it keep funding it. Funds is told and shows the closure as a
 * banner on the vehicle. Who approves a contract, and the minimum investment,
 * are vehicle terms set in the funds console — not here.
 */
export default function ProductAccessPage(): JSX.Element {
  const [rows, setRows] = useState<ProductAccess[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setRows(await productAccessService.list());
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Could not load products.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div style={{ padding: 24, color: tokens.textPrimary }}>
      <PageHeader
        title="New clients by product"
        subtitle="Close a product to new clients. Clients who already hold it keep contributing and withdrawing; nobody new can sign its contract or receive a proposal for it."
      />
      {err ? <div style={errBox}>{err}</div> : null}
      {loading ? (
        <p style={{ color: tokens.textMuted }}>Loading…</p>
      ) : (
        <div style={{ display: 'grid', gap: 12, maxWidth: 760 }}>
          {rows.map((row) => (
            <ProductAccessRow key={row.product_id} row={row} onSaved={reload} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProductAccessRow({
  row,
  onSaved,
}: Readonly<{ row: ProductAccess; onSaved: () => Promise<void> }>): JSX.Element {
  const [closing, setClosing] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (closed: boolean): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await productAccessService.setClosed(row.product_id, closed, closed ? reason.trim() : null);
      setClosing(false);
      setReason('');
      await onSaved();
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Could not save the change.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={card}>
      <div style={headerRow}>
        <div>
          <strong style={{ fontSize: 15 }}>{row.product_name}</strong>
          <span style={{ color: tokens.textMuted, fontSize: 12, marginLeft: 8 }}>
            {row.asset_class ?? '—'}
            {row.is_active ? '' : ' · inactive'}
            {` · shown to ${row.persons_shown ?? 0} ${(row.persons_shown ?? 0) === 1 ? 'person' : 'people'}`}
          </span>
          {/* What became of each person shown the product, derived from facts. */}
          <div style={{ color: tokens.textMuted, fontSize: 12, marginTop: 2 }}>
            {`Accepted ${row.accepted ?? 0} · Declined ${row.declined ?? 0} · Has not accepted ${row.not_accepted ?? 0}`}
          </div>
        </div>
        <StatusTag closed={row.closed} />
      </div>
      <ClosureDetail row={row} />
      {err ? <p style={{ color: tokens.errorText, fontSize: 12, margin: '8px 0 0' }}>{err}</p> : null}
      {closing ? (
        <CloseForm
          reason={reason}
          busy={busy}
          onReason={setReason}
          onCancel={() => setClosing(false)}
          onConfirm={() => void submit(true)}
        />
      ) : (
        <div style={{ marginTop: 10 }}>
          {row.closed ? (
            <button type="button" disabled={busy} onClick={() => void submit(false)} style={btnSecondary}>
              {busy ? 'Saving…' : 'Reopen to new clients'}
            </button>
          ) : (
            <button type="button" onClick={() => setClosing(true)} style={btnSecondary}>
              Close to new clients
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function StatusTag({ closed }: Readonly<{ closed: boolean }>): JSX.Element {
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        padding: '2px 10px',
        borderRadius: 999,
        background: closed ? tokens.errorBg : tokens.surfaceMuted,
        color: closed ? tokens.errorText : tokens.successText,
      }}
    >
      {closed ? 'Closed to new clients' : 'Open'}
    </span>
  );
}

function ClosureDetail({ row }: Readonly<{ row: ProductAccess }>): JSX.Element | null {
  const notMapped = row.mapped_to_funds ? null : (
    <p style={noteText}>Not mapped to a funds vehicle — the funds console will not show a closure.</p>
  );
  if (!row.closed) return notMapped;
  const since = row.closed_at ? new Date(row.closed_at).toLocaleString(readerLocale()) : '';
  return (
    <>
      <p style={{ fontSize: 13, margin: '8px 0 0' }}>{row.closed_reason}</p>
      <p style={noteText}>
        Since {since}
        {row.closed_by_email ? ` · by ${row.closed_by_email}` : ''}
      </p>
      {notMapped}
    </>
  );
}

function CloseForm({
  reason,
  busy,
  onReason,
  onCancel,
  onConfirm,
}: Readonly<{
  reason: string;
  busy: boolean;
  onReason: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}>): JSX.Element {
  const tooShort = reason.trim().length < REASON_MIN;
  return (
    <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
      <label style={labelStyle}>
        <span>Reason (recorded in the audit log and shown in the funds console)</span>
        <textarea
          value={reason}
          maxLength={500}
          rows={3}
          disabled={busy}
          onChange={(e) => onReason(e.target.value)}
          style={inputStyle}
        />
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" disabled={busy || tooShort} onClick={onConfirm} style={btnPrimary}>
          {busy ? 'Saving…' : 'Close to new clients'}
        </button>
        <button type="button" disabled={busy} onClick={onCancel} style={btnSecondary}>
          Cancel
        </button>
      </div>
    </div>
  );
}

const card: React.CSSProperties = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 12,
  padding: 16,
};
const headerRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};
const noteText: React.CSSProperties = { fontSize: 12, color: tokens.textMuted, margin: '4px 0 0' };
const labelStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: tokens.textLabel,
};
const inputStyle: React.CSSProperties = {
  background: tokens.surfaceMuted,
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '8px 10px',
  color: tokens.textPrimary,
  fontFamily: 'inherit',
};
const btnPrimary: React.CSSProperties = {
  background: tokens.gold, color: tokens.navy, border: 'none', borderRadius: 8,
  padding: '8px 16px', fontWeight: 600, cursor: 'pointer',
};
const btnSecondary: React.CSSProperties = {
  background: 'transparent', color: tokens.textPrimary, border: `1px solid ${tokens.border}`,
  borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer',
};
const errBox: React.CSSProperties = {
  background: tokens.errorBg, border: `1px solid ${tokens.errorBorder}`, color: tokens.errorText,
  padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 13,
};
